import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle, Loader2, Mail } from 'lucide-react';

const VerifyEmailPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying | success | error
  const [error, setError] = useState('');

  const token = useMemo(() => {
    const p = new URLSearchParams(location.search);
    return p.get('token');
  }, [location.search]);

  useEffect(() => {
    const run = async () => {
      if (!token) {
        setStatus('error');
        setError('Missing verification token.');
        return;
      }

      try {
        const apiBase = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${apiBase}/api/verify/confirm`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data?.detail || 'Verification failed');
        }

        setStatus('success');
        toast.success('Email verified. You can now claim the Ambassador Pass.');

        // Send user to landing so they can claim.
        setTimeout(() => navigate('/#pricing'), 1200);
      } catch (e) {
        setStatus('error');
        setError(e.message || 'Verification failed');
      }
    };

    run();
  }, [token, navigate]);

  return (
    <div className="min-h-screen bg-[var(--isp-page)] text-[var(--isp-heading)] flex items-center justify-center px-4">
      <div className="max-w-md w-full  border border-slate-800 bg-slate-900/40 p-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center">
            <Mail className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold">Verify your email</h1>
            <p className="text-sm text-[var(--isp-muted)]">This helps secure your account and unlock promotions.</p>
          </div>
        </div>

        {status === 'verifying' && (
          <div className="flex items-center gap-3 text-[var(--isp-muted)]">
            <Loader2 className="w-5 h-5 animate-spin" />
            Verifying…
          </div>
        )}

        {status === 'success' && (
          <div className="flex items-center gap-3 text-green-400">
            <CheckCircle className="w-5 h-5" />
            Verified! Redirecting…
          </div>
        )}

        {status === 'error' && (
          <div className="text-red-400">
            <p className="font-semibold">Verification failed</p>
            <p className="text-sm text-red-300 mt-1">{error}</p>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="mt-4 inline-flex items-center justify-center w-full rounded-xl bg-slate-800 hover:bg-slate-700 transition-colors py-2.5 text-sm font-semibold"
            >
              Back to home
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyEmailPage;
